import React, { useEffect, useMemo, useState } from 'react';

import { UseOutletContextAccounting } from '../Accounting';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ReportDA } from '../../../../services/admin/report.service';
import { FiscalYearDA } from '../../../../services/admin/fiscal.year.service';
import { TrialBalanceRow } from '../../../../models/report.model';
import { FiscalYearLike } from '../../../../scripts/fiscal.year.previous.script';
import { formatMoney, amountClass, tabularStyle } from '../../../../scripts/format.value.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';

import AppPreloader from '../../../../components/Preloader';

export interface TrialBalancePageProps {};

const TrialBalancePage: React.FunctionComponent<TrialBalancePageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextAccounting()
  const {lang} = StateLanguage()

  const [fiscalYears, setFiscalYears] = useState<FiscalYearLike[]>([])
  const [selectedFY, setSelectedFY] = useState<FiscalYearLike | null>(null)
  const [rows, setRows] = useState<TrialBalanceRow[] | null>(null)
  const [level, setLevel] = useState<string>('')
  const [sortBy, setSortBy] = useState<string>('code')
  const [sortOrder, setSortOrder] = useState<string>('asc')
  const [loading, setLoading] = useState(false)

  const locale = (lang.code ?? 'fr').substring(0, 2)

  useEffect(() => {
    setRoute && setRoute({path: {root: lang.labels.accounting, branch: lang.labels.trialBalance}, company: false})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (companyForUser) {
      loadFiscalYearsAndInit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    if (companyForUser && selectedFY && fiscalYears.length > 0) {
      loadReport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFY, level, sortBy, sortOrder])

  const loadFiscalYearsAndInit = async () => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id) return

    try {
      const response = await FiscalYearDA.getFiscalYears(id)
      if (response.status === 200) {
        const data = response.data
        const items: FiscalYearLike[] = Array.isArray(data) ? data : data?.items ?? data?.fiscal_years ?? []
        setFiscalYears(items)

        if (items.length > 0) {
          const today = new Date()
          const current = items.find(fy => new Date(fy.start_date) <= today && new Date(fy.end_date) >= today)
            ?? items.sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime())[0]

          setSelectedFY(current)
        }
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const loadReport = async () => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id || !selectedFY) return

    const from = selectedFY.start_date.substring(0, 10)
    const to = selectedFY.end_date.substring(0, 10)

    setLoading(true)
    setRows(null)

    try {
      const opts: { level?: string, sort_by?: string, sort_order?: string } = {}
      if (level) opts.level = level
      if (sortBy) opts.sort_by = sortBy
      if (sortOrder) opts.sort_order = sortOrder

      const response = await ReportDA.getTrialBalance(id, from, to, opts)
      if (response.status === 200) {
        const data = response.data
        setRows(Array.isArray(data) ? data : data?.items ?? data?.rows ?? [])
      } else {
        setRows([])
      }
    } catch (error) {
      errorWebHandler(lang, error)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  const handleFYChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const fyId = event.target.value
    const fy = fiscalYears.find(f => (f.id ?? f._id) === fyId)
    if (fy) setSelectedFY(fy)
  }

  const handleLevelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setLevel(event.target.value)
  }

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(event.target.value)
  }

  const handleSortOrderChange = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
  }

  const selectedFYId = selectedFY?.id ?? selectedFY?._id ?? ''

  const totals = useMemo(() => {
    if (!rows || rows.length === 0) return null
    return {
      opening_balance: rows.reduce((s, r) => s + r.opening_balance, 0),
      total_debits: rows.reduce((s, r) => s + r.total_debits, 0),
      total_credits: rows.reduce((s, r) => s + r.total_credits, 0),
      closing_balance: rows.reduce((s, r) => s + r.closing_balance, 0),
    }
  }, [rows])

  const isBalanced = totals ? Math.abs(totals.total_debits - totals.total_credits) < 0.01 : true

  return (
    <div className="w-100 h-100">
      { companyForUser && selectedFY
        ?
        <>
          <div className="card mb-5">
            <div className="card-header">
              <div className="card-title">
                <h2>{lang.labels.trialBalance}</h2>
              </div>
              <div className="card-toolbar">
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center">
                    <label className="fw-bold me-2 text-nowrap">{lang.labels.period}:</label>
                    <select
                      className="form-select w-250px"
                      value={selectedFYId}
                      onChange={handleFYChange}
                    >
                      { fiscalYears.map((fy) => {
                        const fyId = fy.id ?? fy._id ?? ''
                        return (
                          <option key={fyId} value={fyId}>
                            {dateFormat(lang.code, fy.start_date)} – {dateFormat(lang.code, fy.end_date)}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                  <div className="d-flex align-items-center">
                    <label className="fw-bold me-2 text-nowrap">{lang.labels.level}:</label>
                    <select
                      className="form-select w-150px"
                      value={level}
                      onChange={handleLevelChange}
                    >
                      <option value="">{lang.labels.allLevels}</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </select>
                  </div>
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select w-175px"
                      value={sortBy}
                      onChange={handleSortChange}
                    >
                      <option value="code">{lang.labels.sortByCode}</option>
                      <option value="closing_balance">{lang.labels.sortByBalance}</option>
                    </select>
                  </div>
                  <button
                    className="btn btn-sm btn-icon btn-light-primary"
                    type="button"
                    onClick={handleSortOrderChange}
                    title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    <span className="svg-icon svg-icon-3">
                      { sortOrder === 'asc'
                        ?
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 4L7 9H11V20H13V9H17L12 4Z" fill="black"/>
                        </svg>
                        :
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 20L17 15H13V4H11V15H7L12 20Z" fill="black"/>
                        </svg>
                      }
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          { loading
            ?
            <AppPreloader />
            :
            rows !== null
            ?
            <>
              { totals &&
                <div className="mb-5">
                  { isBalanced
                    ?
                    <span className="badge badge-success fs-7 px-4 py-2">{lang.labels.balanced}</span>
                    :
                    <span className="badge badge-danger fs-7 px-4 py-2">
                      {lang.labels.notBalanced} ({formatMoney(totals.total_debits - totals.total_credits)})
                    </span>
                  }
                </div>
              }

              <div className="card mb-5">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table align-middle table-row-dashed gy-2 mb-0">
                      <thead className="border-bottom">
                        <tr className="text-muted text-uppercase fw-bolder fs-7">
                          <th className="min-w-80px text-start">{lang.labels.account}</th>
                          <th className="min-w-200px text-start">{lang.labels.description}</th>
                          <th className="min-w-120px text-end">{lang.labels.openingBalance}</th>
                          <th className="min-w-120px text-end">{lang.labels.debit}</th>
                          <th className="min-w-120px text-end">{lang.labels.credit}</th>
                          <th className="min-w-120px text-end">{lang.labels.closingBalance}</th>
                        </tr>
                      </thead>
                      { rows.length > 0
                        ?
                        <>
                          <tbody className="text-secondary">
                            { rows.map((row, index) => (
                              <tr key={index}>
                                <td className="font-monospace">{row.code}</td>
                                <td>{(row.name as Record<string, string>)[locale] ?? row.name.en ?? row.name.fr ?? ''}</td>
                                <td className={`text-end text-nowrap ${amountClass(row.opening_balance)}`} style={tabularStyle}>{formatMoney(row.opening_balance)}</td>
                                <td className={`text-end text-nowrap ${amountClass(row.total_debits)}`} style={tabularStyle}>{formatMoney(row.total_debits)}</td>
                                <td className={`text-end text-nowrap ${amountClass(row.total_credits)}`} style={tabularStyle}>{formatMoney(row.total_credits)}</td>
                                <td className={`text-end text-nowrap ${amountClass(row.closing_balance)}`} style={tabularStyle}>{formatMoney(row.closing_balance)}</td>
                              </tr>
                            ))}
                          </tbody>
                          { totals &&
                            <tfoot className="border-top">
                              <tr className="fw-bolder fs-5">
                                <td colSpan={2}>{lang.labels.total}</td>
                                <td className={`text-end text-nowrap ${amountClass(totals.opening_balance)}`} style={tabularStyle}>{formatMoney(totals.opening_balance)}</td>
                                <td className={`text-end text-nowrap ${amountClass(totals.total_debits)}`} style={tabularStyle}>{formatMoney(totals.total_debits)}</td>
                                <td className={`text-end text-nowrap ${amountClass(totals.total_credits)}`} style={tabularStyle}>{formatMoney(totals.total_credits)}</td>
                                <td className={`text-end text-nowrap ${amountClass(totals.closing_balance)}`} style={tabularStyle}>{formatMoney(totals.closing_balance)}</td>
                              </tr>
                            </tfoot>
                          }
                        </>
                        :
                        <tbody>
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-5">{lang.labels.noRecordsFound}</td>
                          </tr>
                        </tbody>
                      }
                    </table>
                  </div>
                </div>
              </div>
            </>
            :
            <div className="card">
              <div className="card-body text-center text-muted py-10">
                {lang.labels.noRecordsFound}
              </div>
            </div>
          }
        </>
        :
        <AppPreloader />
      }
    </div>
  )
};

export default TrialBalancePage;
